import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWizard from './components/LayoutWizard';
import { LayoutsV2ErrorBoundary } from '../LayoutsV2ErrorBoundary';
import { createLayout, listVenues, mapZonesForSave } from '../api';
import { buildTemplateZones } from './components/layouteditor/utils/layoutTemplates';
import {
  DEFAULT_GRID_COLS,
  GRID_ROWS as DEFAULT_GRID_ROWS,
} from './components/layouteditor/utils/seatUtils';
const AdvancedSeatEditor = lazy(() => import('./components/layouteditor/AdvancedSeatEditor'));

export function LayoutsV2Create() {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    venueId: '',
    type: 'SEAT_PLAN',
    description: '',
    isActive: true,
    useZones: undefined,
    templateId: 'blank',
  });

  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seats, setSeats] = useState([]);
  const [zones, setZones] = useState([]);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [naming, setNaming] = useState(null);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      const rows = await listVenues();
      setVenues(rows);
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const applyTemplateIfNeeded = () => {
    const isArea = formData.type === 'AREA_LAYOUT' || formData.type === 'MIXED';
    const wantsZones = formData.useZones === true || isArea;
    if (!wantsZones) return;

    const templateId =
      formData.templateId || (formData.type === 'AREA_LAYOUT' ? 'amphitheater' : 'blank');

    if (templateId === 'blank') {
      if (isArea) {
        setFormData((prev) => ({ ...prev, useZones: true }));
      }
      return;
    }

    const generated = buildTemplateZones(templateId, {
      gridCols: DEFAULT_GRID_COLS,
      gridRows: DEFAULT_GRID_ROWS,
    });
    setZones(generated);
    setSeats([]);
    setFormData((prev) => ({ ...prev, useZones: true, templateId }));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      applyTemplateIfNeeded();
    }
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        venueId: formData.venueId,
        type: formData.type,
        description: formData.description,
        isActive: formData.isActive,
        useZones: formData.useZones,
        seats,
        zones: mapZonesForSave(zones),
        settings: {
          backgroundImage: backgroundImage || null,
          naming: naming || null,
          templateId: formData.templateId || null,
        },
      };

      const saved = await createLayout(payload);
      navigate(`/layouts-v2/${saved.id}`);
    } catch (err) {
      console.error('Error creating layout:', err);
      setError(err.message || 'Layout kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      id: 1,
      title: 'Layout Bilgileri',
      description: 'Temel bilgileri girin',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: 2,
      title:
        formData.type === 'AREA_LAYOUT'
          ? 'Alan / Blok Tasarımı'
          : formData.useZones
            ? 'Zone & Koltuk Tasarımı'
            : 'Koltuk Tasarımı',
      description:
        formData.type === 'AREA_LAYOUT'
          ? 'Şablon veya çizimle alan bloklarını düzenle'
          : formData.useZones
            ? "Zone'ları çiz ve koltuk ekle"
            : 'Koltuk düzeni oluştur',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      id: 3,
      title: 'İnceleme & Kaydet',
      description: 'Sonucu kontrol edin',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="layouts-v2-root min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => navigate('/layouts-v2')}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Yeni Layout Oluştur</h1>
                <p className="text-sm text-gray-500">
                  Venue&apos;nuz için profesyonel koltuk düzeni tasarlayın
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                      currentStep === step.id
                        ? 'bg-blue-600 text-white'
                        : currentStep > step.id
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      step.icon
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-12 h-0.5 ml-2 ${
                        currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {(() => {
        if (currentStep === 2) {
          return (
            <div className="w-full">
              {error && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 mt-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                    <div className="flex-1">
                      <p className="text-red-800">{error}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <LayoutsV2ErrorBoundary>
                <Suspense
                  fallback={
                    <div className="layouts-v2-root p-8 text-center text-gray-800">
                      Editör yükleniyor…
                    </div>
                  }
                >
                  <AdvancedSeatEditor
                    onSeatsCreate={setSeats}
                    existingSeats={seats}
                    zones={zones}
                    useZones={
                      formData.useZones === true ||
                      formData.type === 'AREA_LAYOUT' ||
                      formData.type === 'MIXED'
                    }
                    layoutType={formData.type}
                    onZonesUpdate={setZones}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    initialBackgroundImage={backgroundImage}
                    onBackgroundChange={setBackgroundImage}
                    initialNaming={naming}
                    onNamingChange={setNaming}
                    initialTemplateId={formData.templateId}
                  />
                </Suspense>
              </LayoutsV2ErrorBoundary>
            </div>
          );
        }

        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                <div className="flex-1">
                  <p className="text-red-800">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700"
                >
                  ×
                </button>
              </div>
            )}

            {currentStep === 1 && (
              <LayoutWizard
                formData={formData}
                venues={venues}
                onInputChange={handleInputChange}
                onNext={handleNext}
              />
            )}

            {currentStep === 3 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Layout İncelemesi</h2>
                  <p className="text-gray-600">
                    Oluşturduğunuz layout&apos;u kaydetmeden önce kontrol edin
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Temel Bilgiler</h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Layout Adı:</span>
                          <span className="font-medium">{formData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Venue:</span>
                          <span className="font-medium">
                            {venues.find((v) => v.id === formData.venueId)?.name || 'Seçilmedi'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tip:</span>
                          <span className="font-medium">
                            {formData.type === 'SEAT_PLAN'
                              ? 'Koltuk Planı'
                              : formData.type === 'AREA_LAYOUT'
                                ? 'Alan Düzeni'
                                : 'Karışık'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Zone Kullanımı:</span>
                          <span className="font-medium">
                            {formData.useZones ? 'Zonelu' : 'Zonesiz'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Durum:</span>
                          <span
                            className={`font-medium ${formData.isActive ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {formData.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">İstatistikler</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-blue-600">{seats.length}</div>
                          <div className="text-sm text-blue-800">Toplam Koltuk</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-purple-600">{zones.length}</div>
                          <div className="text-sm text-purple-800">Zone Sayısı</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Layout Önizleme</h3>
                    <div className="bg-gray-50 rounded-lg p-4 h-64 flex items-center justify-center">
                      {seats.length > 0 ? (
                        <p className="text-sm text-gray-600">Layout hazır!</p>
                      ) : (
                        <p className="text-sm text-gray-400">Koltuk eklenmedi</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Geri Dön
                  </button>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading || !formData.name || !formData.venueId}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Kaydediliyor...' : "Layout'u Kaydet"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default LayoutsV2Create;
